import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AdminLayout } from './components/layout/AdminLayout';
import { RouteGuard } from './components/RouteGuard';
import {
  AnalyticsSkeleton,
  DetailPageSkeleton,
  LiveOrdersSkeleton,
  TablePageSkeleton,
} from './components/ui/PageSkeletons';
import { SkipLink } from './components/ui/SkipLink';
import { ToastContainer } from './components/ui/ToastContainer';
import { AuthProvider, getDefaultRoute, useAuth } from './contexts/AuthContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { ToastProvider, useToast } from './contexts/ToastContext';
import { initKeyboardNavigation } from './utils/accessibility';
import { initHighContrastMode } from './utils/highContrastMode';

// Eager load critical pages
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';
import { UnauthorizedPage } from './pages/UnauthorizedPage';

// Lazy load non-critical pages
const AdvertisementsPage = lazy(() =>
  import('./pages/AdvertisementsPage').then((m) => ({ default: m.AdvertisementsPage }))
);
const AnalyticsPage = lazy(() =>
  import('./pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage }))
);
const RestaurantPerformancePage = lazy(() =>
  import('./pages/RestaurantPerformancePage').then((m) => ({ default: m.RestaurantPerformancePage }))
);
const CustomersAnalyticsPage = lazy(() =>
  import('./pages/CustomersAnalyticsPage').then((m) => ({ default: m.CustomersAnalyticsPage }))
);
const CancellationsAnalyticsPage = lazy(() =>
  import('./pages/CancellationsAnalyticsPage').then((m) => ({ default: m.CancellationsAnalyticsPage }))
);
const DeliveryAnalyticsPage = lazy(() =>
  import('./pages/DeliveryAnalyticsPage').then((m) => ({ default: m.DeliveryAnalyticsPage }))
);
const MenuPage = lazy(() => import('./pages/MenuPage').then((m) => ({ default: m.MenuPage })));
const OrdersPage = lazy(() =>
  import('./pages/OrdersPage').then((m) => ({ default: m.OrdersPage }))
);
const PendingOrdersPage = lazy(() =>
  import('./pages/PendingOrdersPage').then((m) => ({ default: m.PendingOrdersPage }))
);
const RestaurantsPage = lazy(() =>
  import('./pages/RestaurantsPage').then((m) => ({ default: m.RestaurantsPage }))
);
const RestaurantViewPage = lazy(() =>
  import('./pages/RestaurantViewPage').then((m) => ({ default: m.RestaurantViewPage }))
);
const UsersPage = lazy(() => import('./pages/UsersPage').then((m) => ({ default: m.UsersPage })));
const ZoneManagementPage = lazy(() =>
  import('./pages/ZoneManagementPage').then((m) => ({ default: m.ZoneManagementPage }))
);
const StaffManagementPage = lazy(() =>
  import('./pages/StaffManagementPage').then((m) => ({ default: m.StaffManagementPage }))
);
const CustomerUserManagementPage = lazy(() =>
  import('./pages/CustomerUserManagementPage').then((m) => ({
    default: m.CustomerUserManagementPage,
  }))
);
const SystemSettingsPage = lazy(() =>
  import('./pages/SystemSettingsPage').then((m) => ({ default: m.SystemSettingsPage }))
);


function RootRedirect() {
  const { staffUser } = useAuth();
  return <Navigate to={getDefaultRoute(staffUser?.role)} replace />;
}

function AppContent() {
  const { toasts, removeToast } = useToast();

  // Initialize keyboard navigation detection and high contrast mode
  useEffect(() => {
    const cleanupKeyboard = initKeyboardNavigation();
    const cleanupHighContrast = initHighContrastMode();

    return () => {
      cleanupKeyboard();
      cleanupHighContrast();
    };
  }, []);

  return (
    <>
      <SkipLink targetId="main-content" />
      <Router>
        <NavigationProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route
              path="/dashboard"
              element={
                <RouteGuard requiredPage="dashboard">
                  <AdminLayout>
                    <ErrorBoundary>
                      <DashboardPage />
                    </ErrorBoundary>
                  </AdminLayout>
                </RouteGuard>
              }
            />
            <Route
              path="/restaurants"
              element={
                <RouteGuard requiredPage="restaurants">
                  <AdminLayout>
                    <ErrorBoundary>
                      <Suspense fallback={<TablePageSkeleton showAction />}>
                        <RestaurantsPage />
                      </Suspense>
                    </ErrorBoundary>
                  </AdminLayout>
                </RouteGuard>
              }
            />
            <Route
              path="/restaurants/:restaurantId"
              element={
                <RouteGuard requiredPage="restaurants">
                  <AdminLayout>
                    <ErrorBoundary>
                      <Suspense fallback={<DetailPageSkeleton />}>
                        <RestaurantViewPage />
                      </Suspense>
                    </ErrorBoundary>
                  </AdminLayout>
                </RouteGuard>
              }
            />
            <Route
              path="/restaurants/:restaurantId/menu"
              element={
                <RouteGuard requiredPage="menu-items">
                  <AdminLayout>
                    <ErrorBoundary>
                      <Suspense fallback={<DetailPageSkeleton />}>
                        <MenuPage />
                      </Suspense>
                    </ErrorBoundary>
                  </AdminLayout>
                </RouteGuard>
              }
            />
            <Route
              path="/orders"
              element={
                <RouteGuard requiredPage="order-management">
                  <AdminLayout>
                    <ErrorBoundary>
                      <Suspense fallback={<TablePageSkeleton />}>
                        <OrdersPage />
                      </Suspense>
                    </ErrorBoundary>
                  </AdminLayout>
                </RouteGuard>
              }
            />
            <Route
              path="/live-orders"
              element={
                <RouteGuard requiredPage="live-orders">
                  <AdminLayout>
                    <ErrorBoundary>
                      <Suspense fallback={<LiveOrdersSkeleton />}>
                        <PendingOrdersPage />
                      </Suspense>
                    </ErrorBoundary>
                  </AdminLayout>
                </RouteGuard>
              }
            />
            <Route
              path="/zones"
              element={
                <RouteGuard requiredPage="zones">
                  <AdminLayout>
                    <ErrorBoundary>
                      <Suspense fallback={<TablePageSkeleton />}>
                        <ZoneManagementPage />
                      </Suspense>
                    </ErrorBoundary>
                  </AdminLayout>
                </RouteGuard>
              }
            />
            <Route
              path="/users"
              element={
                <RouteGuard requiredPage="customer-management">
                  <AdminLayout>
                    <ErrorBoundary>
                      <Suspense fallback={<TablePageSkeleton />}>
                        <UsersPage />
                      </Suspense>
                    </ErrorBoundary>
                  </AdminLayout>
                </RouteGuard>
              }
            />
            <Route
              path="/analytics"
              element={
                <RouteGuard requiredPage="analytics">
                  <AdminLayout>
                    <ErrorBoundary>
                      <Suspense fallback={<AnalyticsSkeleton />}>
                        <AnalyticsPage />
                      </Suspense>
                    </ErrorBoundary>
                  </AdminLayout>
                </RouteGuard>
              }
            />
            <Route
              path="/analytics/restaurant-performance"
              element={
                <RouteGuard requiredPage="analytics">
                  <AdminLayout>
                    <ErrorBoundary>
                      <Suspense fallback={<AnalyticsSkeleton />}>
                        <RestaurantPerformancePage />
                      </Suspense>
                    </ErrorBoundary>
                  </AdminLayout>
                </RouteGuard>
              }
            />
            <Route
              path="/analytics/customers"
              element={
                <RouteGuard requiredPage="analytics">
                  <AdminLayout>
                    <ErrorBoundary>
                      <Suspense fallback={<AnalyticsSkeleton />}>
                        <CustomersAnalyticsPage />
                      </Suspense>
                    </ErrorBoundary>
                  </AdminLayout>
                </RouteGuard>
              }
            />
            <Route
              path="/analytics/cancellations"
              element={
                <RouteGuard requiredPage="analytics">
                  <AdminLayout>
                    <ErrorBoundary>
                      <Suspense fallback={<AnalyticsSkeleton />}>
                        <CancellationsAnalyticsPage />
                      </Suspense>
                    </ErrorBoundary>
                  </AdminLayout>
                </RouteGuard>
              }
            />
            <Route
              path="/analytics/delivery"
              element={
                <RouteGuard requiredPage="analytics">
                  <AdminLayout>
                    <ErrorBoundary>
                      <Suspense fallback={<AnalyticsSkeleton />}>
                        <DeliveryAnalyticsPage />
                      </Suspense>
                    </ErrorBoundary>
                  </AdminLayout>
                </RouteGuard>
              }
            />
            <Route
              path="/advertisements"
              element={
                <RouteGuard requiredPage="advertisements">
                  <AdminLayout>
                    <ErrorBoundary>
                      <Suspense fallback={<TablePageSkeleton showAction />}>
                        <AdvertisementsPage />
                      </Suspense>
                    </ErrorBoundary>
                  </AdminLayout>
                </RouteGuard>
              }
            />
            <Route
              path="/staff-management"
              element={
                <RouteGuard requiredPage="staff-management">
                  <AdminLayout>
                    <ErrorBoundary>
                      <Suspense fallback={<TablePageSkeleton showAction />}>
                        <StaffManagementPage />
                      </Suspense>
                    </ErrorBoundary>
                  </AdminLayout>
                </RouteGuard>
              }
            />
            <Route
              path="/customer-management"
              element={
                <RouteGuard requiredPage="customer-management">
                  <AdminLayout>
                    <ErrorBoundary>
                      <Suspense fallback={<TablePageSkeleton />}>
                        <CustomerUserManagementPage />
                      </Suspense>
                    </ErrorBoundary>
                  </AdminLayout>
                </RouteGuard>
              }
            />
            <Route
              path="/system-settings"
              element={
                <RouteGuard requiredPage="system-settings">
                  <AdminLayout>
                    <ErrorBoundary>
                      <Suspense fallback={<TablePageSkeleton />}>
                        <SystemSettingsPage />
                      </Suspense>
                    </ErrorBoundary>
                  </AdminLayout>
                </RouteGuard>
              }
            />
            <Route path="/" element={<RootRedirect />} />
          </Routes>
        </NavigationProvider>
      </Router>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
